// Step 4: Metadata per track group
import { Tag as TagIcon, Plus, X, Sparkles, Loader2 } from 'lucide-react';
import { useState, useCallback } from 'react';
import { useUploadWizardContext } from '../context/UploadWizardContext';
import { Tag, TagType } from '@types';
import { cn } from '@utils/classNames';
import { useToast } from '@components/ui';
import { useAuthStore } from '@stores/authStore';
import { UploadGroupCard } from '../components/UploadGroupCard';
import { useUploadGroups } from '../hooks/useUploadGroups';
import {
  analyzeGroupsInParallel,
  GroupAnalysisState,
} from '../utils/analyzeGroupsInParallel';

const AVAILABLE_TAGS: Tag[] = [
  { id: '1', label: 'Hip Hop', type: TagType.GENRE },
  { id: '2', label: 'Trap', type: TagType.GENRE },
  { id: '3', label: 'R&B', type: TagType.GENRE },
  { id: '4', label: 'Pop', type: TagType.GENRE },
  { id: '5', label: 'Dark', type: TagType.MOOD },
  { id: '6', label: 'Energetic', type: TagType.MOOD },
  { id: '7', label: 'Chill', type: TagType.MOOD },
  { id: '8', label: 'Aggressive', type: TagType.MOOD },
  { id: '9', label: 'Piano', type: TagType.INSTRUMENT },
  { id: '10', label: 'Guitar', type: TagType.INSTRUMENT },
  { id: '11', label: '808', type: TagType.INSTRUMENT },
  { id: '12', label: 'Synth', type: TagType.INSTRUMENT },
];

export const Step3Metadata: React.FC = () => {
  const { data, setData } = useUploadWizardContext();
  const { groups, updateGroup, getFileSize } = useUploadGroups();
  const [customTag, setCustomTag] = useState('');
  const [analysisStates, setAnalysisStates] = useState<Map<string, GroupAnalysisState>>(new Map());
  const [isAnalyzingAll, setIsAnalyzingAll] = useState(false);
  const { success, error: showError } = useToast();
  const { user } = useAuthStore();

  const toggleGroupTag = (groupId: string, tag: Tag) => {
    const group = groups.find((g) => g.id === groupId);
    if (!group) return;
    const isSelected = group.tags.find((t) => t.id === tag.id);
    const tags = isSelected
      ? group.tags.filter((t) => t.id !== tag.id)
      : [...group.tags, tag];
    updateGroup(groupId, { tags });
  };

  const addCustomTagToGroup = (groupId: string) => {
    if (!customTag.trim()) return;
    const group = groups.find((g) => g.id === groupId);
    if (!group) return;
    const newTag: Tag = {
      id: `custom-${Date.now()}`,
      label: customTag.trim(),
      type: TagType.CUSTOM,
    };
    updateGroup(groupId, { tags: [...group.tags, newTag] });
    setCustomTag('');
  };

  const removeGroupTag = (groupId: string, tagId: string) => {
    const group = groups.find((g) => g.id === groupId);
    if (!group) return;
    updateGroup(groupId, { tags: group.tags.filter((t) => t.id !== tagId) });
  };

  const analyzeGroup = useCallback(
    async (groupId: string) => {
      if (!user?.id) {
        showError('User not authenticated');
        return;
      }
      const group = groups.find((g) => g.id === groupId);
      if (!group) return;

      setAnalysisStates((prev) => {
        const next = new Map(prev);
        next.set(groupId, { groupId, status: 'analyzing' });
        return next;
      });

      const results = await analyzeGroupsInParallel([group], data.files, user.id, setAnalysisStates);
      const result = results.get(groupId);

      if (result?.error) {
        showError('Auto-detect failed', result.error);
      } else if (result?.bpm || result?.musicalKey) {
        updateGroup(groupId, {
          bpm: result.bpm ?? group.bpm,
          musicalKey: result.musicalKey ?? group.musicalKey,
        });
        success(`Detected: ${result.bpm ? `${result.bpm} BPM` : ''} ${result.musicalKey ?? ''}`.trim());
      }
    },
    [groups, data.files, user?.id, showError, success, updateGroup]
  );

  const analyzeAllGroups = useCallback(async () => {
    if (!user?.id || groups.length === 0) return;
    setIsAnalyzingAll(true);
    try {
      const results = await analyzeGroupsInParallel(
        groups,
        data.files,
        user.id,
        setAnalysisStates
      );
      setData((prev) => ({
        groups: prev.groups.map((g) => {
          const result = results.get(g.id);
          if (!result || result.error) return g;
          return {
            ...g,
            bpm: result.bpm ?? g.bpm,
            musicalKey: result.musicalKey ?? g.musicalKey,
          };
        }),
      }));
      const successCount = [...results.values()].filter((r) => !r.error).length;
      if (successCount > 0) {
        success(`Analyzed ${successCount} group${successCount !== 1 ? 's' : ''}`);
      }
    } finally {
      setIsAnalyzingAll(false);
    }
  }, [groups, data.files, user?.id, setData, success]);

  return (
    <div className="space-y-3 sm:space-y-4 w-full">
      <div className="text-center mb-2 sm:mb-3">
        <h2 className="font-dela text-lg sm:text-xl text-white mb-1">Metadata</h2>
        <p className="text-white/60 font-inter text-sm sm:text-base">
          Add tags, BPM, and key per track group (optional)
        </p>
        {groups.length > 1 && (
          <button
            type="button"
            onClick={analyzeAllGroups}
            disabled={isAnalyzingAll}
            className={cn(
              'mt-2 inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-lg font-inter text-xs sm:text-sm transition-all',
              isAnalyzingAll
                ? 'bg-white/10 text-white/40 cursor-not-allowed'
                : 'bg-gradient-to-r from-[#30d870]/70 to-[#00ff61]/70 text-white hover:from-[#30d870] hover:to-[#00ff61] border border-primary/30'
            )}
          >
            {isAnalyzingAll ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Analyzing all...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Auto-detect all groups
              </>
            )}
          </button>
        )}
      </div>

      <div className="space-y-3">
        {groups.map((group) => {
          const analysisState = analysisStates.get(group.id);
          const isAnalyzing = analysisState?.status === 'analyzing';

          return (
            <UploadGroupCard
              key={group.id}
              group={group}
              mode="metadata"
              getFileSize={getFileSize}
            >
              <div className="space-y-3 pt-2 border-t border-white/5">
                <div>
                  <label className="flex items-center gap-2 mb-2 font-codec font-bold text-xs sm:text-sm text-white">
                    <TagIcon className="w-3.5 h-3.5" />
                    Tags
                    <span className="px-1.5 py-0.5 rounded-full bg-white/10 text-[10px] text-white/60 font-inter font-normal">
                      Optional
                    </span>
                  </label>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {AVAILABLE_TAGS.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => toggleGroupTag(group.id, tag)}
                        className={cn(
                          'px-2.5 py-1 min-h-[32px] rounded-lg font-inter text-xs transition-all',
                          group.tags.find((t) => t.id === tag.id)
                            ? 'bg-gradient-to-r from-[#30d870]/70 to-[#00ff61]/70 text-white border border-primary/30'
                            : 'bg-white/10 text-white/70 hover:bg-white/20 border border-white/10'
                        )}
                      >
                        {tag.label}
                      </button>
                    ))}
                  </div>
                  {group.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {group.tags.map((tag) => (
                        <div
                          key={tag.id}
                          className="flex items-center gap-1 px-2 py-0.5 bg-primary/20 rounded-full text-xs text-white"
                        >
                          {tag.label}
                          <button type="button" onClick={() => removeGroupTag(group.id, tag.id)}>
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customTag}
                      onChange={(e) => setCustomTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addCustomTagToGroup(group.id)}
                      placeholder="Custom tag..."
                      className="flex-1 min-h-[40px] px-2.5 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-xs font-inter placeholder:text-white/40 focus:outline-none focus:border-primary/50"
                    />
                    <button
                      type="button"
                      onClick={() => addCustomTagToGroup(group.id)}
                      disabled={!customTag.trim()}
                      className="px-2.5 min-h-[40px] rounded-lg bg-white/10 text-white/70 hover:bg-white/20 disabled:opacity-50 flex items-center"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="font-codec font-bold text-xs sm:text-sm text-white">
                      BPM & Key
                    </label>
                    <button
                      type="button"
                      onClick={() => analyzeGroup(group.id)}
                      disabled={isAnalyzing || isAnalyzingAll}
                      className={cn(
                        'flex items-center gap-1 px-2 py-1.5 min-h-[36px] rounded-lg font-inter text-xs transition-all',
                        isAnalyzing || isAnalyzingAll
                          ? 'bg-white/10 text-white/40 cursor-not-allowed'
                          : 'bg-gradient-to-r from-[#30d870]/70 to-[#00ff61]/70 text-white border border-primary/30'
                      )}
                    >
                      {isAnalyzing ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Sparkles className="w-3 h-3" />
                      )}
                      Auto-detect
                    </button>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="number"
                      min="50"
                      max="220"
                      value={group.bpm}
                      onChange={(e) => updateGroup(group.id, { bpm: e.target.value })}
                      placeholder="BPM (e.g. 140)"
                      className="flex-1 min-h-[44px] px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm font-inter placeholder:text-white/40 focus:outline-none focus:border-primary/50"
                    />
                    <input
                      type="text"
                      value={group.musicalKey}
                      onChange={(e) =>
                        updateGroup(group.id, { musicalKey: e.target.value.toUpperCase() })
                      }
                      placeholder="Key (e.g. C#m)"
                      maxLength={4}
                      className="flex-1 min-h-[44px] px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm font-inter placeholder:text-white/40 focus:outline-none focus:border-primary/50"
                    />
                  </div>
                  {analysisState?.status === 'error' && (
                    <p className="text-red-400 text-xs mt-1">{analysisState.error}</p>
                  )}
                </div>
              </div>
            </UploadGroupCard>
          );
        })}
      </div>

      <div className="space-y-2 pt-2 border-t border-white/10">
        <label className="block font-codec font-bold text-sm text-white">
          BeatStars & Collaborator
          <span className="ml-2 px-1.5 py-0.5 rounded-full bg-white/10 text-[10px] text-white/60 font-inter font-normal">
            Optional
          </span>
        </label>
        <p className="text-white/40 font-inter text-xs">
          Applied to all uploaded files when you confirm.
        </p>
        <input
          type="text"
          value={data.collaborator}
          onChange={(e) => setData({ collaborator: e.target.value })}
          placeholder="Collaborator (name or handle)"
          className="w-full min-h-[44px] px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm font-inter placeholder:text-white/40 focus:outline-none focus:border-primary/50"
        />
        <input
          type="url"
          value={data.beatstarsSellUrl}
          onChange={(e) => setData({ beatstarsSellUrl: e.target.value })}
          placeholder="BeatStars sell URL"
          className="w-full min-h-[44px] px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm font-inter placeholder:text-white/40 focus:outline-none focus:border-primary/50"
        />
        <input
          type="text"
          value={data.beatstarsPrice}
          onChange={(e) => setData({ beatstarsPrice: e.target.value })}
          placeholder="Price (e.g. 49 €)"
          className="w-full min-h-[44px] px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm font-inter placeholder:text-white/40 focus:outline-none focus:border-primary/50"
        />
      </div>
    </div>
  );
};
